import { Injectable } from '@angular/core';
import { DefaultUrlSerializer, PRIMARY_OUTLET, type ParamMap } from '@angular/router';
import { hasPermission } from '@sb/shared/permission/util';
import type { PermissionState } from '@sb/shared/permission/util';
import { inOrderPlainDateRange, maxToday, plainDate, type DateRange } from '@shiftbase-com/ts-shared/utilities';

import { fromPlainDateRange, toPlainDateRange } from './payroll-scope';
import { payrollStepDefinitions } from './payroll-steps';
import { payrollDepartmentPermissionCheck } from './payroll.access';
import { type PayrollStepId } from './payroll.model';

/** The prepare payroll flow, as the router's primary path (no leading slash) and as a URL. */
export const payrollFlowPath = 'payroll/prepare';
export const payrollFlowUrl = `/${payrollFlowPath}`;

/** The payroll overview, the page in front of the flow. */
export const payrollOverviewPath = 'payroll';
export const payrollOverviewUrl = `/${payrollOverviewPath}`;

const urlSerializer = new DefaultUrlSerializer();

function primaryPath(url: string): string {
  const segments = urlSerializer.parse(url).root.children[PRIMARY_OUTLET]?.segments ?? [];

  return segments.map((segment) => segment.path).join('/');
}

/**
 * Whether a serialized URL is the prepare payroll flow. Parsed rather than matched on the raw
 * string: a URL carries query params, matrix params or a secondary outlet, so
 * `startsWith('/payroll/prepare')` would both miss `/payroll;x=1/prepare` and wrongly claim a
 * route merely prefixed with it.
 */
export function isPayrollFlowUrl(url: string): boolean {
  return primaryPath(url) === payrollFlowPath;
}

/**
 * The fallback for "leave the payroll flow, but there's nowhere to go back to" — a deep link
 * straight into `/payroll`, or a fresh tab. The established fallback for "the feature the
 * manager was on is no longer available" elsewhere in the app (see subscription-quota.guard.ts).
 * `PayrollReturnRouteService.exitUrl()` is what most exits actually navigate to; this is only
 * its own last resort.
 */
export const payrollExitUrl = '/dashboard/my-overview';

/**
 * Where a manager goes when they leave the payroll flow: wherever they actually came from (the
 * overview, the timesheet priority view, a deep link into the middle of some other flow, ...),
 * not a fixed route. Its own
 * module because both the page and the progress panel send managers out of the flow, and the
 * panel cannot read it off the page: `SidePanelService`'s `COMPONENT_MAP` already imports the
 * panel, so a panel -> page import would close a cycle.
 *
 * Has no subscription of its own — `AuthenticatedComponent`'s existing `NavigationEnd` handler
 * feeds it via `track()`, since that one is already running for the whole authenticated session
 * before payroll is ever entered. A service that only started listening once payroll first
 * injected it would have missed the very navigation it needs to remember.
 */
@Injectable({ providedIn: 'root' })
export class PayrollReturnRouteService {
  #lastNonFlowUrl: string | null = null;

  #lastOutsidePayrollUrl: string | null = null;

  #lastOverviewUrl: string | null = null;

  /**
   * Payroll's own URL is skipped, so this keeps whatever was current immediately before entering
   * the flow for as long as the manager stays in it — nothing inside the flow itself (a scope
   * change, a step change) navigates away from `/payroll`.
   *
   * Takes a URL already serialized by the router (`NavigationEnd.urlAfterRedirects`), which is why
   * {@link exitUrl} is a string rather than a commands array: the routes managers arrive from carry
   * matrix params (`/timesheet/team/day;date=2026-08-27`), and handing a whole URL to
   * `Router.navigate` as one command percent-encodes the `;`, producing a path that matches no
   * route. Round-tripping it through `navigateByUrl` keeps params, matrix segments and secondary
   * outlets intact. SHB-24138 review.
   */
  track(url: string): void {
    const path = primaryPath(url);

    if (!isPayrollFlowUrl(url)) {
      this.#lastNonFlowUrl = url;
    }

    if (path !== payrollOverviewPath && !path.startsWith(`${payrollOverviewPath}/`)) {
      this.#lastOutsidePayrollUrl = url;
    }

    if (path === payrollOverviewPath) {
      this.#lastOverviewUrl = url;
    }
  }

  /** Where Done goes: the page the flow was opened from, the overview included. */
  exitUrl(): string {
    return this.#lastNonFlowUrl ?? payrollExitUrl;
  }

  /**
   * Where losing the labs flag mid-session sends the manager: the last page outside `/payroll`,
   * since the overview sits behind the same flag. Moving between the flow and the overview stays
   * under one parent route, so its access guard would not run again to turn them away.
   */
  outsidePayrollUrl(): string {
    return this.#lastOutsidePayrollUrl ?? payrollExitUrl;
  }

  /**
   * The overview as the manager last left it — its period and departments — so going back from
   * the flow does not reset its filters. The bare overview when it was never visited, such as
   * when the flow was opened from the timesheet.
   */
  overviewUrl(): string {
    return this.#lastOverviewUrl ?? payrollOverviewUrl;
  }
}

/**
 * The query params a second entry point (the timesheet actions menu) hands `/payroll` the period
 * and departments it already knows, so the page can start the run itself rather than the entry
 * point writing straight into `PayrollRunService` (a root singleton) before navigating — a
 * navigation that never lands (a guard redirect, the flag flipping mid-click) would otherwise
 * strand that write with nothing to clean it up. One shared shape for both the builder and the
 * parser below keeps the two sides from drifting on param names or format.
 */
export interface PayrollEntryParams {
  from: string;
  to: string;
  departments?: string;
  /** The step the run opens on, for links to one step. Absent opens the first step. */
  step?: PayrollStepId;
}

/** `departments` is omitted rather than sent empty when the caller found none it could hand over — see `parsePayrollEntryParams`. */
export function buildPayrollEntryParams(
  range: DateRange,
  departmentIds: string[],
  stepId: PayrollStepId | null = null,
): PayrollEntryParams {
  const plainRange = toPlainDateRange(range);

  return {
    from: plainRange.start,
    to: plainRange.end,
    ...(departmentIds.length > 0 && { departments: departmentIds.join(',') }),
    ...(stepId && { step: stepId }),
  };
}

/**
 * The entry params are hand-editable, and the lock step renders a named row per department of the
 * run's scope — so an id the manager holds no payroll permission on would disclose departments and
 * locations outside their reach. The entry point that builds these params applies the same filter
 * before navigating (see `#startPayrollRun` in timesheet-header.component.ts); re-applying it on
 * arrival stops the URL widening the scope past what the header's department menu would offer.
 */
export function departmentsWithPayrollPermission(departmentIds: string[], permissionState: PermissionState): string[] {
  return departmentIds.filter((departmentId) =>
    hasPermission({ ...payrollDepartmentPermissionCheck, departments: [departmentId] }, permissionState),
  );
}

/**
 * The departments the payroll header starts on before a run exists: the ones the entry point
 * handed over, or — when it handed none this manager may act on, which is the side nav's case
 * (no params at all) as much as a stale link's — the ones they already have selected app-wide.
 *
 * Both candidates go through {@link departmentsWithPayrollPermission}, so neither path can seed
 * the header with a department the manager could not have picked out of the menu by hand. The
 * fallback keys off "nothing permitted came from the URL" rather than "the URL named nothing",
 * so a link naming only departments this manager has since lost access to still lands them on a
 * usable scope instead of an empty control.
 */
export function payrollPrefillDepartmentIds(
  entryDepartmentIds: string[],
  appSelectedDepartmentIds: string[],
  permissionState: PermissionState,
): string[] {
  const fromEntryPoint = departmentsWithPayrollPermission(entryDepartmentIds, permissionState);

  return fromEntryPoint.length > 0
    ? fromEntryPoint
    : departmentsWithPayrollPermission(appSelectedDepartmentIds, permissionState);
}

/**
 * Normalises the pair through `inOrderPlainDateRange`: these params are hand-editable, and a
 * reversed range enumerates no days at all, which leaves the grid with department rows but no
 * per-day entries to render them from.
 *
 * Both endpoints are also capped at today, the same ceiling the header's date control enforces.
 * This entry point carries whatever range the timesheet had selected, which routinely reaches
 * into the future, and a hint resolves a run outright when it arrives with departments — so
 * without the cap the URL would seed periods the control itself would refuse. Capping before the
 * reorder keeps a wholly-future range from resolving to a reversed one; it collapses to today.
 */
export function parsePayrollEntryParams(params: ParamMap): {
  dateHint: DateRange | null;
  departmentIds: string[];
  stepId: PayrollStepId | null;
} {
  const from = params.get('from');
  const to = params.get('to');
  const dateHint =
    from && to
      ? fromPlainDateRange(inOrderPlainDateRange({ start: maxToday(plainDate(from)), end: maxToday(plainDate(to)) }))
      : null;

  return {
    dateHint,
    departmentIds: params.get('departments')?.split(',') ?? [],
    stepId: payrollStepIdFrom(params.get('step')),
  };
}

// Hand-editable like the rest, so anything that names no step is read as no step at all.
function payrollStepIdFrom(value: string | null): PayrollStepId | null {
  return payrollStepDefinitions.find((definition) => definition.id === value)?.id ?? null;
}
